import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ErrorBoundary, Retry } from '@components/common';
import { getUserProfile } from '@store/common/selectors';
import OverviewDatePicker from './components/datepicker/datepicker';
import DashboardTabs from '../common/Dashboard/DashboardTabs';
import {
  Wrapper,
  DashboardPanel,
  TimelineTitleWrapper,
  SessionTableTitleWrapper,
} from './styled';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import moment from 'moment-timezone';
import '@conviva/organism-components/styles.css';
import { UserTimeline, ItemRenderer } from '@conviva/organism-components';
import { useGetTimelineEventListNew } from './utils/queryHooks';
import { Button, Space, Tooltip } from 'antd';
import { useAtom, useAtomValue } from 'jotai';
import {
  selectedEventsAtom,
  highlightedSessionIdsAtom,
  savedUserTimelineStateAtom,
} from './atom';
import track, { CLICK_TIMELINE_CUSTOMIZE_APPLY, perfTrack } from '@src/utils/sensor/track';
import { allDimensionsListSelector } from '@src/store/common/dimensionList/selectors';
import { getFlowTimelineEventLabelNew, processEventAttributeWithGF } from './utils/dataUtils';
import { usePageTitle } from '@src/hooks';
import { InfoCircleOutlined, ShareAltOutlined } from '@ant-design/icons';
import { ColumnShape } from 'react-base-table';
import { FlowTag } from './components/flow-tag';
import { FlowSelector } from './components/flow-selector';
import { FlowInstance } from './types';
// Shared hooks
import { useIds } from './hooks/shared/useIds';
import { useTimelineSummary } from './hooks/shared/useTimelineSummary';
import { useShare } from './hooks/shared/useShare';
import { useIdType } from './hooks/shared/useIdType';
import { useErrors } from './hooks/shared/useErrors';
import { useGlobalFilters } from './hooks/shared/useGlobalFilters';
import { useInterval } from './hooks/shared/useInterval';
import { useEnableNW } from './hooks/shared/useEnableNW';
import { useEventFilter } from './hooks/shared/useEventFilter';
import { useItemList } from './hooks/shared/useItemList';
import { useEnableUserId } from './hooks/shared/useEnableUserId';
import { useFlowTimelineMeta } from './hooks/shared/useFlowTimelineMeta';
// Flow-specific hooks
import { useSelectedSession } from './hooks/flow/useSelectedSessionNew';
import { useCustomize } from './hooks/flow/useCustomizeNew';
import { useSessionsWithFlowInstances } from './hooks/flow/useSessionsWithFlowInstances';
import { useSelectedFlow } from './hooks/flow/useSelectedFlow';
import { useSessionTableData } from './hooks/flow/useSessionTableNew';
import { loadUserTimelineState } from '@utils/userTimelineStorage';

import SharingPopover from '@components/common/Sharing/SharingPopover';
import DPISummary from '../viewer-module/AISummary/DPISummary';

interface IProps {
  id: string | number;
  flowId?: string | number;
  viewerId?: string | string[];
  clientId: string;
  interval?: string;
  impacted?: boolean;
  source?: string;
  globalFilters?: any;
  idType?: string;
  eventNames?: string[];
}

const EmptyArray: any[] = [];

export const getClientIdSuffix = () => {
  return (
    <Tooltip title="Multiple Client IDs detected. Select them one by one to see the corresponding sessions.">
      <InfoCircleOutlined style={{ color: 'blue' }} />
    </Tooltip>
  );
};
const AppSessionTimeline: React.FC<IProps> = ({
  id = 'user-timeline',
  flowId,
  viewerId = '',
  clientId,
  interval: defaultInterval,
  impacted = false,
  source,
  globalFilters,
  idType: existedIdType,
  eventNames = [],
}) => {
  const timezone = moment().tz() ?? 'UTC';
  const { customer_id } = useSelector(getUserProfile);
  const selectedTrendMetric = useSelector(selectedMetricSelector);
  const [selectedEvents, setSelectedEvents] = useAtom(selectedEventsAtom);
  const [savedTimelineState, setSavedTimelineState] = useAtom(savedUserTimelineStateAtom);

  const dimensionsList = useSelector(allDimensionsListSelector);
  const { flowMetadata } = useFlowTimelineMeta(flowId);
  const { idType, handleIdSelectChanged } = useIdType({
    flowId,
    viewerId,
    clientId,
    existedIdType,
  });
  const { interval, timeSpan, handleDateChange } = useInterval({ defaultInterval });
  const { nwEnabled, handleNWToggleChange } = useEnableNW();
  const { userIdEnabled, handleUserIdToggleChange } = useEnableUserId();
  const { idGroupData, selectedClientId, selectedViewerId, idLoading, shouldShowUserIdToggle } =
    useIds({
      id,
      flowId,
      flowIdentifier: flowMetadata?.identifier,
      viewerId,
      clientId,
      selectedTrendMetric: flowMetadata ? flowMetadata.metrics.init : selectedTrendMetric,
      interval,
      customer_id,
      idType,
      handleIdSelectChanged,
    });
  const { customized, customizeData, transformedCustomizeData, onCustomizedChange } = useCustomize({
    flowMetadata,
    savedMetrics: savedTimelineState?.selectedMetrics,
  });

  const sessionTableFields = customized;

  const { summaryData, summaryLoading, summaryError, isFetchedAfterMount, refetchSummary } =
    useTimelineSummary({
      viewerId: selectedViewerId,
      clientId: selectedClientId,
      interval,
    });

  const sessionTableRes = useSessionTableData({
    viewerId: selectedViewerId,
    clientId: selectedClientId,
    interval,
    eventNames,
    flowMetadata,
    fields: sessionTableFields,
    filter: globalFilters,
    options: customizeData,
    impacted,
  });

  const {
    flowToSessions,
    sessionToFlows,
    flowOptions,
    selectedFlowIdentifiers,
    setSelectedFlowIdentifiers,
    isLoading: isSessionsWithFlowInstancesLoading,
  } = useSessionsWithFlowInstances({
    viewerId: selectedViewerId,
    clientId: selectedClientId,
    interval,
    filter: globalFilters,
    sessions: sessionTableRes.data,
  });

  const { selectedSession, handleSelectedSessionChange } = useSelectedSession({
    impacted,
    sessionTableData: sessionTableRes.data,
    viewerId: selectedViewerId,
    interval,
    clientId: selectedClientId,
    idGroupData,
  });

  const { selectedFlow, handleSelectedFlowChange } = useSelectedFlow(flowToSessions);
  const highlightedSessionIds = useAtomValue(highlightedSessionIdsAtom);
  const flowSessionIds = useMemo(() => {
    return selectedFlow
      ? flowToSessions?.[selectedFlow.flow_identifier]?.[
          `${selectedFlow.flow_start_timestamp}-${selectedFlow.flow_end_timestamp}`
        ]
      : [];
  }, [selectedFlow, flowToSessions]);

  useEffect(() => {
    if (flowMetadata && flowMetadata.identifier) {
      setSelectedFlowIdentifiers([flowMetadata.identifier]);
    }
  }, [flowMetadata, setSelectedFlowIdentifiers]);

  const {
    data: timelineData,
    isFetching: eventListLoading,
    error: eventListError,
    refetch: refetchEventList,
  } = useGetTimelineEventListNew(
    selectedViewerId,
    interval,
    selectedClientId,
    flowSessionIds && flowSessionIds.length > 0 ? flowSessionIds : selectedSession?.sessionId ?? '',
    nwEnabled,
    globalFilters,
    userIdEnabled,
  );

  const { error, renderError } = useErrors({
    summaryError,
    sessionTableError: sessionTableRes.error,
    eventListError,
    refetchSummary,
    refetchSessionTable: sessionTableRes.refetch,
    refetchEventList,
  });

  const {
    visible,
    isFetching: shareLoading,
    url,
    id: shareId,
    openShare,
    handleVisibleChange,
  } = useShare({
    interval,
    clientId: selectedClientId,
    viewerId: selectedViewerId,
    source,
    globalFilters,
    idType,
    eventNames,
    flowId,
  });

  useEffect(() => {
    perfTrack();
  }, [isFetchedAfterMount]);

  // Load saved user timeline state from localStorage on mount
  useEffect(() => {
    const loadedState = loadUserTimelineState();
    if (loadedState) {
      setSavedTimelineState(loadedState);
    }
  }, [setSavedTimelineState]);

  const sessionFlows = useMemo(() => {
    if (!selectedSession?.sessionId || !sessionToFlows) {
      return [];
    }

    if (selectedSession.sessionId.includes('/')) {
      const sessionIds = selectedSession.sessionId.split('/');
      const allFlows = new Set();

      sessionIds.forEach((sessionId) => {
        const flows = sessionToFlows[sessionId] || [];
        flows.forEach((flow) => allFlows.add(flow));
      });

      return Array.from(allFlows);
    }

    return sessionToFlows[selectedSession.sessionId] || [];
  }, [sessionToFlows, selectedSession]);

  const eventList = timelineData?.eventList ?? EmptyArray;
  const resGF = useGlobalFilters({ impacted, globalFilters, dimensionsList });
  const { eventFilterOn, handleEventFilterOnChange } = useEventFilter({ eventList });
  const { timelineItems } = useItemList({ eventList, selectedSession });

  const resEventList = useMemo(() => {
    return eventList
      .map((item) => {
        const res =
          impacted || (shouldShowUserIdToggle && userIdEnabled)
            ? processEventAttributeWithGF(
                item.eventAttributes ?? {},
                resGF,
                dimensionsList as any[],
                item.eventName,
                eventNames,
              )
            : undefined;

        const flowEvent = getFlowTimelineEventLabelNew(item, sessionFlows);

        return {
          ...item,
          ...res,
          flowEvent,
        };
      })
      ?.filter((item) => !!item);
  }, [
    eventList,
    impacted,
    shouldShowUserIdToggle,
    userIdEnabled,
    resGF,
    dimensionsList,
    eventNames,
    sessionFlows,
  ]);

  const itemRenderers: ItemRenderer = useMemo(() => ({}), []);

  const handleFlowSelectionChange = useCallback(
    (selectedIdentifiers: string[]) => {
      setSelectedFlowIdentifiers(selectedIdentifiers);
    },
    [setSelectedFlowIdentifiers],
  );

  const renderSessionTableTitle = () => {
    return (
      <SessionTableTitleWrapper>
        <span>User Timeline Index</span>
        <FlowSelector
          flowOptions={flowOptions}
          selectedFlows={selectedFlowIdentifiers}
          onSelectionChange={handleFlowSelectionChange}
          disabled={false}
          placeholder="Filter flows"
          loading={isSessionsWithFlowInstancesLoading}
        />
      </SessionTableTitleWrapper>
    );
  };

  const renderHeaderRight = () => {
    return (
      <Space direction="vertical">
        <Space direction="horizontal">
          <OverviewDatePicker dateChangeFn={handleDateChange} selectedTimeSpan={timeSpan} />
          <SharingPopover
            url={url}
            id={shareId}
            loading={shareLoading}
            open={visible}
            handleOpen={handleVisibleChange}
            cid={customer_id}
          >
            <Button
              icon={<ShareAltOutlined />}
              onClick={openShare}
              style={{
                borderRadius: '3px',
                width: '28px',
                height: '28px',
              }}
            />
          </SharingPopover>
        </Space>
      </Space>
    );
  };

  const clickApplyInCustomizeDropdown = (value: string) => {
    track({ event: CLICK_TIMELINE_CUSTOMIZE_APPLY, data: { customized: value } });
  };

  usePageTitle({ text: 'User Timeline' });

  const timelineRef = useRef<any>(null);

  const handleClickFlowTag = useCallback(
    (flow: FlowInstance) => {
      handleSelectedFlowChange(flow, sessionTableRes.data);
      timelineRef.current?.setSearchValue(`${flow.flow_name} Initial Event`);
    },
    [handleSelectedFlowChange, sessionTableRes.data, timelineRef],
  );

  const enhancedSessionTableData = useMemo(() => {
    if (!sessionTableRes.data || !sessionToFlows || Object.keys(sessionToFlows).length === 0) {
      return sessionTableRes.data;
    }
    return sessionTableRes.data.map((session) => ({
      ...session,
      flowInstances: sessionToFlows[session.sessionId] || [],
    }));
  }, [sessionTableRes.data, sessionToFlows]);

  const sessionTableCustomizeColumns = useCallback(
    (columns: ColumnShape[]) => {
      const result = [...columns];
      if (sessionToFlows && Object.keys(sessionToFlows).length > 0) {
        const insertColumn: ColumnShape = {
          title: 'Contains',
          dataKey: 'flowInstances',
          key: 'flow_instances',
          width: 100,
          flexShrink: 0,
          cellRenderer: ({ cellData }) => {
            const flowInstances = cellData || [];
            return <FlowTag flows={flowInstances} onClickFlow={handleClickFlowTag} />;
          },
        };
        result.splice(1, 0, insertColumn); // Insert at the position with index 1
      }

      return result;
    },
    [sessionToFlows, handleClickFlowTag],
  );

  return (
    <Wrapper>
      <DashboardPanel>
        <DashboardTabs id={id as number} />
      </DashboardPanel>
      <Retry isLoading={idLoading || summaryLoading}>
        <UserTimeline
          ref={timelineRef}
          sessionTableTitle={renderSessionTableTitle()}
          timelineData={resEventList}
          errorPlaceholder={error ? renderError() : undefined}
          timelineConfig={{
            timelineItems,
            itemRenderers,
          }}
          timelineLoading={eventListLoading || sessionTableRes.loading}
          idGroupData={idGroupData}
          summaryData={summaryData}
          timelineTitle={
            <TimelineTitleWrapper>
              <span>User Timeline</span>
              <DPISummary
                interval={interval}
                viewerId={selectedViewerId}
                clientId={selectedClientId}
                sessionId={selectedSession.sessionId}
                summaryData={summaryData}
                timelineData={timelineData}
              />
            </TimelineTitleWrapper>
          }
          minWidth={780}
          // session table
          sessionTableCustomizeColumns={sessionTableCustomizeColumns}
          sessionTableData={enhancedSessionTableData}
          selectedSession={selectedSession}
          highlightedSessions={highlightedSessionIds}
          onSelectedSessionChange={handleSelectedSessionChange}
          sessionTableLoading={sessionTableRes.loading}
          customizeData={transformedCustomizeData}
          customized={customized}
          onCustomizedChange={onCustomizedChange}
          onApplyClicked={clickApplyInCustomizeDropdown}
          // event list
          headerRightComp={renderHeaderRight()}
          enableNetworkRequest={nwEnabled}
          onNetworkRequestToggleChange={handleNWToggleChange}
          showUserIdToggle={shouldShowUserIdToggle}
          enableUserId={shouldShowUserIdToggle && userIdEnabled}
          onUserIdChange={handleUserIdToggleChange}
          timezone={timezone}
          selectedEvents={selectedEvents}
          onSelectedEventsChange={setSelectedEvents}
          eventFilterOn={eventFilterOn}
          onEventFilterOnChange={handleEventFilterOnChange}
          // impacted
          impacted={impacted}
          globalFilters={resGF}
          impactedType={source === 'emp' ? 'Paths/Funnels' : 'Trends'}
          eventNames={eventNames}
        />
      </Retry>
    </Wrapper>
  );
};

export default React.memo(AppSessionTimeline);
